// Phone numbers this browser was told are new, and has not verified since.
//
// The phone code screen offers "Use Google instead" only to a number with no
// account yet (send-otp's isNewUser). But the first send creates an
// unverified record for the number, so every later send, from a reload, a
// return visit or a switch to the Google tab and back, reports the number as
// known and the line disappeared for exactly the people who never got in.
//
// The backend could say "known but never verified", but anyone can call
// send-otp for any number, and that answer would tell a stranger which numbers
// have a real QLearn account (today's answer only says a code was asked for
// before). So the browser remembers its own answer instead: a number it was
// told is new still counts as new here until it is verified here, for up to
// 7 days (abandoned attempts are retried soon, if at all). Another device
// still gets the cautious answer (no line).
//
// Known gap: a number started here and then verified on another device still
// counts as new here until the entry lapses or this browser logs out (logout
// clears localStorage), so it may be offered Google after 30s without a code.
// Closing it needs the server to vouch per caller (e.g. a signed started-here
// cookie checked against phoneVerified), not a public "unverified" flag.
//
// Stored as a 16-bit hash with a time, not the digits: with ~10^10 numbers per
// value it cannot be traced back to a phone number, and a false match among
// five entries (~0.008%) only shows an extra Google line. Storage that is blocked or corrupt reads as "not
// started here", the cautious answer.

export const PHONE_CODE_START_KEY = 'phoneCodeStartedHere';
export const PHONE_CODE_START_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 5;

// FNV-1a folded to 16 bits: enough to tell five numbers apart, too short to reverse.
const hashNumber = (phoneNumber) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < phoneNumber.length; i += 1) {
    hash ^= phoneNumber.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return ((hash ^ (hash >>> 16)) & 0xffff).toString(36);
};

const readEntries = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PHONE_CODE_START_KEY));
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    const live = parsed.filter((entry) => entry
      && typeof entry.h === 'string'
      && typeof entry.t === 'number'
      && now - entry.t < PHONE_CODE_START_TTL_MS);
    // Drop lapsed or malformed entries now, so storage keeps the 7-day promise.
    if (live.length !== parsed.length) writeEntries(live);
    return live;
  } catch {
    return [];
  }
};

const writeEntries = (entries) => {
  try {
    if (entries.length) {
      window.localStorage.setItem(PHONE_CODE_START_KEY, JSON.stringify(entries));
    } else {
      window.localStorage.removeItem(PHONE_CODE_START_KEY);
    }
  } catch {
    // Blocked storage: nothing remembered, so later sends get the cautious answer.
  }
};

export const rememberPhoneCodeStart = (phoneNumber) => {
  if (!phoneNumber) return;
  const h = hashNumber(phoneNumber);
  const others = readEntries().filter((entry) => entry.h !== h);
  writeEntries([{ h, t: Date.now() }, ...others].slice(0, MAX_ENTRIES));
};

export const startedPhoneCodeHere = (phoneNumber) => {
  if (!phoneNumber) return false;
  const h = hashNumber(phoneNumber);
  return readEntries().some((entry) => entry.h === h);
};

export const forgetPhoneCodeStart = (phoneNumber) => {
  if (!phoneNumber) return;
  const h = hashNumber(phoneNumber);
  const entries = readEntries();
  const kept = entries.filter((entry) => entry.h !== h);
  if (kept.length !== entries.length) writeEntries(kept);
};
